/**
 * Thumbnail + name card for the Alerts Timeline's left group panel — one per
 * product, rendered via `timelineGroup.renderGroupLabel`. Reuses the same
 * lazy `/products/:id/images` fetch ListViewProductCard uses, since the
 * products list endpoint doesn't include a thumbnail URL.
 */
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Battery, ExternalLink } from "lucide-react";
import { ImageLoader } from "../../../ui/components/ImageLoader";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import type { ProductListItem } from "../../lib/api/products";

const API_BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "http://localhost:3003/api";

type ImageUrls = { thumb: string; card: string };

/**
 * Module-level cache, keyed by productId — the Timeline's hover-card tooltip
 * mounts/unmounts this component every time the cursor crosses in and out of
 * an alert's active time window (the whole tooltip portal is conditionally
 * rendered). Caching the resolved URLs (or `null` for "no image") makes every
 * mount after the first successful one for a given product synchronous.
 */
const imageUrlCache = new Map<string, ImageUrls | null>();

/**
 * In-flight-request dedup, also keyed by productId. Caching the *resolved
 * value* alone (above) isn't enough while the cursor is actively scrubbing
 * across the Timeline: mounts/unmounts happen faster than one round trip, so
 * without this, every remount before the first fetch resolves started its
 * OWN independent fetch — and if each of those also got unmounted before ITS
 * fetch resolved, no live instance ever survived to see a result, so the
 * thumbnail stayed gray until the user stopped scrubbing entirely. Sharing
 * one in-flight promise per product means there's ever only one real
 * request, and every mount (no matter how short-lived) subscribes to the
 * same result — whichever instance happens to be mounted when it resolves
 * gets the image, and the cache above then makes every later mount instant.
 */
const imageUrlFetches = new Map<string, Promise<ImageUrls | null>>();

/**
 * Fires a `new Image()` request completely outside React/component
 * lifecycle, so the browser starts decoding and (memory-)caching the bytes
 * immediately and keeps that cache regardless of how many times a consuming
 * `<img>` element gets destroyed and recreated afterward.
 *
 * This matters because the whole hover-card tooltip — everything inside
 * TimelineView's `ReactDOM.createPortal(...)` call, including every
 * ProductGroupCard and its `<img>` tags — unmounts completely every time the
 * cursor crosses a gap with no alert active at all (`activeAtCursor.length`
 * hits 0), which happens constantly during normal scrubbing across a
 * multi-product timeline. Caching the resolved URL string (imageUrlCache,
 * above) makes React skip re-fetching the *metadata*, but a fresh `<img
 * src>` on a brand-new DOM node still has to actually load the *image
 * bytes* — and if the next portal teardown interrupts that load before it
 * finishes (the browser cancels an in-flight image request when its <img>
 * element is removed from the DOM), no instance ever sees it complete. A
 * standalone Image() object isn't tied to any component's DOM, so its load
 * can't be interrupted that way, and once it succeeds, the browser can serve
 * the real <img> tags from cache almost instantly however many times they
 * get recreated.
 */
function preloadImage(url: string) {
  if (typeof window === "undefined") return;
  const img = new Image();
  img.src = url;
}

function fetchProductImages(productId: string, accessToken: string): Promise<ImageUrls | null> {
  const inFlight = imageUrlFetches.get(productId);
  if (inFlight) return inFlight;

  const promise = (async () => {
    const res = await fetch(`${API_BASE}/products/${productId}/images`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw new Error(`Failed to load product images (${res.status})`);
    const data = (await res.json()) as {
      images?: Array<{ url: string; thumbUrl?: string | null; cardUrl?: string | null; sortOrder: number }>;
    };
    const first = (data.images ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)[0];
    if (!first) return null;
    const urls = { thumb: first.thumbUrl ?? first.url, card: first.cardUrl ?? first.url };
    preloadImage(urls.card);
    if (urls.thumb !== urls.card) preloadImage(urls.thumb);
    return urls;
  })();

  imageUrlFetches.set(productId, promise);
  // Only the in-flight dedup entry is cleared here — imageUrlCache is written
  // by the caller only on success — so a later mount can retry after a
  // transient network error instead of being stuck with a permanently-cached
  // failure.
  promise.finally(() => imageUrlFetches.delete(productId));
  return promise;
}

export interface ProductGroupCardProps {
  product: ProductListItem | undefined;
  productId: string;
  accessToken: string | null;
  /**
   * 'stacked' (default): thumbnail + two-line name/subtitle, sized for the
   * Timeline's narrow left group panel. 'inline': a single line — small
   * thumbnail + "Name - City - Model" — for contexts with more horizontal
   * room (the grouped hover-card tooltip) where a row-height-matching header
   * matters more than showing the subtitle on its own line.
   */
  variant?: "stacked" | "inline";
}

export function ProductGroupCard({ product, productId, accessToken, variant = "stacked" }: ProductGroupCardProps) {
  const navigate = useNavigate();
  const [imgUrls, setImgUrls] = React.useState<ImageUrls | null>(() => imageUrlCache.get(productId) ?? null);

  React.useEffect(() => {
    if (!accessToken) {
      setImgUrls(null);
      return;
    }
    if (imageUrlCache.has(productId)) {
      setImgUrls(imageUrlCache.get(productId) ?? null);
      return;
    }
    let cancelled = false;
    fetchProductImages(productId, accessToken)
      .then((urls) => {
        imageUrlCache.set(productId, urls);
        if (!cancelled) setImgUrls(urls);
      })
      .catch(() => {
        // gallery unavailable — fall back to the placeholder icon. Not cached
        // as null: a transient network error shouldn't permanently suppress
        // retrying on the next mount.
      });
    return () => {
      cancelled = true;
    };
  }, [productId, accessToken]);

  const subtitle = product?.productModel?.name;

  if (variant === "inline") {
    const label = [product?.name ?? productId, product?.city, product?.productModel?.name]
      .filter(Boolean)
      .join(" - ");
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <div style={{ width: 16, height: 16, borderRadius: 3, overflow: "hidden", flexShrink: 0, background: "var(--color-muted)" }}>
          {imgUrls ? (
            <ImageLoader src={imgUrls.card} placeholderSrc={imgUrls.thumb} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Battery size={10} style={{ opacity: 0.55 }} />
            </div>
          )}
        </div>
        <Text level="body-small" weight="bold" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </Text>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, height: "100%", minWidth: 0 }}>
      <div style={{ width: 28, height: 28, borderRadius: 4, overflow: "hidden", flexShrink: 0, background: "var(--color-muted)" }}>
        {imgUrls ? (
          <ImageLoader src={imgUrls.card} placeholderSrc={imgUrls.thumb} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Battery size={16} style={{ opacity: 0.55 }} />
          </div>
        )}
      </div>
      <div style={{ minWidth: 0, overflow: "hidden", flex: "1 1 auto" }}>
        <Text level="body-small" weight="bold" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {product?.name ?? productId}
        </Text>
        {subtitle && (
          <span style={{ display: "block", fontSize: 11, color: "var(--color-fgMuted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {subtitle}
          </span>
        )}
      </div>
      <Button
        type="button"
        color="neutral"
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={`Open ${product?.name ?? "product"} detail page`}
        title="Open product detail page"
        style={{ flexShrink: 0, marginTop: -2, marginRight: -4 }}
        onClick={(e) => {
          e.stopPropagation();
          void navigate({ to: "/workspace/products/$productId", params: { productId } });
        }}
      >
        <ExternalLink size={14} />
      </Button>
    </div>
  );
}
